import { inject, DOCUMENT } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { environment } from '../../../environments/environment';
import { AppRoutes } from 'shared-utils';
import { AuthService, TokenInfo } from 'auth';
import { UserService } from 'shared-data-access';

type QueryParams = { tenantId?: string; floatingLinkId?: string };
type TokenData = QueryParams & { isTokenFloatingDataPresent: boolean };
type QueryData = QueryParams & { isQueryFloatingDataPresent: boolean };
type AccessEvaluationParams = {
  isAuthenticated: boolean;
  tokenData: TokenData;
  router: Router;
  window: Window;
  queryParams: QueryData;
  authService: AuthService;
};

export const floatingModuleAuthFn: CanActivateFn = async (): Promise<boolean> => {
  const router: Router = inject(Router);
  const authService: AuthService = inject(AuthService);
  const userService: UserService = inject(UserService);
  const window: Window = inject(DOCUMENT).defaultView;

  try {
    const isAuthenticated: boolean = await authService.isAuthenticated();

    return evaluateAccess({
      isAuthenticated,
      window,
      tokenData: getTokenData(userService),
      queryParams: getQueryParams(window),
      router,
      authService,
    });
  } catch (e) {
    return router.navigate([AppRoutes.InvalidLink]);
  }
};

async function evaluateAccess({
  isAuthenticated,
  tokenData,
  router,
  window,
  authService,
  queryParams,
}: AccessEvaluationParams): Promise<boolean> {
  if (shouldAllowAccessWithoutQuery(isAuthenticated, queryParams)) {
    return true;
  }

  if (shouldAllowAccessWithToken(tokenData, queryParams)) {
    return true;
  }

  if (!queryParams.floatingLinkId || !queryParams.tenantId) {
    return navigateToInvalidLink(router);
  }

  await handleInvalidToken(authService, window, queryParams as QueryData);
  return true;
}

function getQueryParams(window: Window): QueryData {
  const paramMap: URLSearchParams = new URLSearchParams(window.location.search);
  const tenantId: string = paramMap.get('tenantUuid');
  const floatingLinkId: string = paramMap.get('linkUuid');

  return { tenantId, floatingLinkId, isQueryFloatingDataPresent: !!(tenantId && floatingLinkId) };
}

function getTokenData(userService: UserService): TokenData {
  const tokenInfo: TokenInfo = userService.getTokenInfo();

  return {
    floatingLinkId: tokenInfo?.link_id,
    tenantId: tokenInfo?.tenant_uuid,
    isTokenFloatingDataPresent: !!(tokenInfo?.link_id && tokenInfo?.tenant_uuid),
  };
}

function shouldAllowAccessWithoutQuery(isAuthenticated: boolean, { isQueryFloatingDataPresent }: QueryData): boolean {
  return isAuthenticated && !isQueryFloatingDataPresent;
}

function shouldAllowAccessWithToken(
  tokenData: TokenData,
  { tenantId, floatingLinkId, isQueryFloatingDataPresent }: QueryData
): boolean {
  const isLinkConsistentWithToken: boolean =
    floatingLinkId === tokenData.floatingLinkId && tenantId === tokenData.tenantId;
  return tokenData.isTokenFloatingDataPresent && (!isQueryFloatingDataPresent || isLinkConsistentWithToken);
}

function navigateToInvalidLink(router: Router): Promise<boolean> {
  return router.navigate([AppRoutes.InvalidLink]);
}

async function handleInvalidToken(authService: AuthService, window: Window, queryData: QueryData): Promise<void> {
  await authService.logout(false);
  const forgerockUrl: URL = getForgerockCustomerLoginJourneyLink(queryData);
  window.location.replace(forgerockUrl);
}

function getForgerockCustomerLoginJourneyLink(config: QueryData): URL {
  const customerFRUrl: URL = new URL(`${environment.forgeRock.baseUrl}/am/XUI/?realm=/alpha`);

  customerFRUrl.searchParams.set('authIndexType', 'service');
  customerFRUrl.searchParams.set('authIndexValue', 'FloatingModuleImpersonatedLogin');
  customerFRUrl.searchParams.set('floatingLinkId', config.floatingLinkId);
  customerFRUrl.searchParams.set('tenantId', config.tenantId);

  return customerFRUrl;
}
